import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./CouponModal.css";
import { couponMeshGradient } from "../utils/couponMeshGradient";
import {
  MODAL_TICKET_PERFORATION_FROM_TOP,
  TICKET_VARIANT_MODAL,
  ticketPath,
  ticketNotchCenterY,
} from "../utils/ticketPath";
import TicketPerforation from "./TicketPerforation";

const MIN_W = 48;
const MIN_H = 48;

/** Must match stub height logic in layout (see CouponModal.css comment). */
const MODAL_STUB_PX = 72;
const CONTENT_TO_LINE_GAP_PX = 12;

const PERFORATION_STROKE = "rgba(43, 52, 103, 0.38)";

const formatReceived = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

/** Unclaimed coupons use status strings like "1 Remaining"; claimed uses "Claimed". */
const isRemainingStatus = (s) =>
  Boolean(s && s !== "Claimed" && /remaining/i.test(s));

const MODAL_SHADOW = "0 5px 15px rgba(0, 0, 0, 0.3)";

/** Degrees; new value each time the modal opens. */
const randomEntranceTiltDeg = () => {
  const sign = Math.random() < 0.5 ? -1 : 1;
  const magnitude = 6 + Math.random() * 15;
  return sign * magnitude;
};

const CouponModal = ({
  isOpen,
  emoji,
  name,
  description,
  status,
  received,
  showBelowLabels = true,
  disableClaimButton = false,
  onClose,
  onClaim,
}) => {
  const panelRef = useRef(null);
  const innerRef = useRef(null);
  const [ticketLayout, setTicketLayout] = useState(null);

  useLayoutEffect(() => {
    if (!isOpen) {
      setTicketLayout(null);
      return undefined;
    }
    const panel = panelRef.current;
    const inner = innerRef.current;
    if (!panel || !inner) return undefined;

    const compute = () => {
      const clipW = panel.clientWidth;
      const innerBottom = inner.offsetTop + inner.offsetHeight;
      const preferredCy = Math.max(
        MODAL_TICKET_PERFORATION_FROM_TOP,
        innerBottom + CONTENT_TO_LINE_GAP_PX
      );
      const cy = ticketNotchCenterY(
        preferredCy + MODAL_STUB_PX,
        TICKET_VARIANT_MODAL,
        preferredCy
      );
      const H = cy + MODAL_STUB_PX;
      setTicketLayout((prev) =>
        prev &&
        prev.cy === cy &&
        prev.H === H &&
        prev.preferredCy === preferredCy &&
        prev.clipW === clipW
          ? prev
          : { cy, H, preferredCy, clipW }
      );
    };

    compute();
    const ro = new ResizeObserver(() => compute());
    ro.observe(inner);
    ro.observe(panel);
    return () => ro.disconnect();
  }, [isOpen, emoji, name, description, status]);

  const entranceTiltDeg = useMemo(
    () => (isOpen ? randomEntranceTiltDeg() : 0),
    [isOpen]
  );

  if (!isOpen) return null;

  const w =
    ticketLayout && ticketLayout.clipW >= MIN_W ? ticketLayout.clipW : 0;
  const path =
    w >= MIN_W && ticketLayout && ticketLayout.H >= MIN_H
      ? ticketPath(
          w,
          ticketLayout.H,
          TICKET_VARIANT_MODAL,
          ticketLayout.preferredCy
        )
      : "";

  const panelStyle =
    ticketLayout != null
      ? {
          height: `${ticketLayout.H}px`,
          minHeight: `${ticketLayout.H}px`,
          maxHeight: `${ticketLayout.H}px`,
          "--perf-y": `${ticketLayout.cy}px`,
        }
      : undefined;

  const receivedLabel = received
    ? `Received ${formatReceived(received)}`
    : null;

  const showRemainingAboveDate = isRemainingStatus(status);
  const statusInStub = showRemainingAboveDate ? "Claim" : status;

  return createPortal(
    <div className="coupon-modal-backdrop" onClick={onClose}>
      <button
        type="button"
        className="coupon-modal-close"
        aria-label="Close"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      >
        ×
      </button>
      <div
        className="coupon-modal-dialog"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="coupon-modal-title"
        aria-describedby={
          description ? "coupon-modal-description" : undefined
        }
      >
        <div className="coupon-modal-rise">
          <div
            className="coupon-modal-tilt"
            style={{
              "--coupon-modal-tilt-from": `${entranceTiltDeg}deg`,
            }}
          >
            <div
              ref={panelRef}
              className="coupon-modal"
              style={panelStyle}
            >
              {path ? (
                <>
                  <div
                    className="coupon-modal-fill"
                    aria-hidden
                    style={{
                      clipPath: `path("${path}")`,
                      WebkitClipPath: `path("${path}")`,
                      background: couponMeshGradient(name),
                      boxShadow: MODAL_SHADOW,
                    }}
                  />
                  <TicketPerforation
                    width={w}
                    height={ticketLayout.H}
                    className="coupon-modal-perforation"
                    variant={TICKET_VARIANT_MODAL}
                    notchCenterY={ticketLayout.preferredCy}
                    stroke={PERFORATION_STROKE}
                  />
                </>
              ) : null}
              <div ref={innerRef} className="coupon-modal-inner">
                <div className="coupon-modal-emoji" aria-hidden>
                  {emoji}
                </div>
                <h2 id="coupon-modal-title" className="coupon-modal-title">
                  {name}
                </h2>
                {description ? (
                  <p
                    id="coupon-modal-description"
                    className="coupon-modal-description"
                  >
                    {description}
                  </p>
                ) : null}
              </div>
              <div className="coupon-modal-status-wrap">
                {showRemainingAboveDate ? (
                  <button
                    type="button"
                    className={
                      disableClaimButton
                        ? "coupon-modal-claim coupon-modal-claim--disabled"
                        : "coupon-modal-claim"
                    }
                    disabled={disableClaimButton}
                    aria-disabled={disableClaimButton ? "true" : "false"}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (disableClaimButton) return;
                      onClaim?.();
                    }}
                  >
                    {statusInStub}
                  </button>
                ) : (
                  <p className="coupon-modal-status">{statusInStub}</p>
                )}
              </div>
            </div>
            {showBelowLabels && (showRemainingAboveDate || receivedLabel) ? (
              <div className="coupon-modal-below-ticket">
                {showRemainingAboveDate ? (
                  <p className="coupon-modal-remaining-above-date">{status}</p>
                ) : null}
                {receivedLabel ? (
                  <p className="coupon-modal-received-below">{receivedLabel}</p>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CouponModal;
